import { Activity } from "lucide-react";
import { ProbeRetry } from "./ProbeRetry";
import { ProbeWaitFix } from "./ProbeWaitFix";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const probeResult = (p) => {
  if (!p?.creation_id) return "no probe yet";
  if (p.result === "ok") return "succeeded, the limit lifted";
  if (p.result === "still limited") return "still limited";
  return p.used ? `sent (${p.phase || "AI call"}), waiting for the answer` : "armed, waiting for the duck's next AI call";
};

const decisionLine = (d) => `${d.outcome}${d.reason ? `: ${d.reason}` : ""}${d.creation_id ? ` · duck ${d.creation_id}` : ""} · ${when(d.at)} · ${d.by}`;

// Probe status next to the AI-limit banner: last/next probe, result, why it waits (with the fix), owner "Retry now".
export const AiLimitProbe = ({ aiLimit, gate, viewer, onRetry, onUnpause, onRaise }) => {
  if (!aiLimit?.active) return null;
  const probe = aiLimit.probe || {};
  const blocked = aiLimit.blocked_by;
  return (
    <section data-testid="ai-probe-status" className="mt-3 rounded-2xl border border-amber-300 bg-card px-4 py-3 text-sm">
      <p className="font-heading font-bold flex items-center gap-2">
        <Activity className="h-4 w-4 text-amber-700" /> Probe status
      </p>
      <dl className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-[auto_1fr]">
        <dt className="text-xs uppercase tracking-widest text-muted-foreground font-mono2">Last probe</dt>
        <dd data-testid="ai-probe-last">
          {probe.creation_id ? `${when(probe.armed_at)} · ${aiLimit.probe_duck_name || probe.creation_id}` : "none yet"}
        </dd>
        <dt className="text-xs uppercase tracking-widest text-muted-foreground font-mono2">Result</dt>
        <dd data-testid="ai-probe-result">{probeResult(probe)}</dd>
        <dt className="text-xs uppercase tracking-widest text-muted-foreground font-mono2">Next probe</dt>
        <dd data-testid="ai-probe-next">{when(aiLimit.next_probe_at)}</dd>
        {aiLimit.decision && (
          <>
            <dt className="text-xs uppercase tracking-widest text-muted-foreground font-mono2">Last decision</dt>
            <dd data-testid="ai-probe-decision" className="text-muted-foreground">{decisionLine(aiLimit.decision)}</dd>
          </>
        )}
      </dl>
      {blocked && (
        <div data-testid="ai-probe-waiting" className="mt-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-900 px-3 py-2">
          <p className="font-bold">Probe waiting: {blocked.text}</p>
          <p className="text-xs mt-0.5">The probe never goes past the spend gate. It runs as soon as the gate allows.</p>
          <ProbeWaitFix blocked={blocked} gate={gate} viewer={viewer} onUnpause={onUnpause} onRaise={onRaise} />
        </div>
      )}
      <ProbeRetry aiLimit={aiLimit} viewer={viewer} onRetry={onRetry} />
    </section>
  );
};
